import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ActivityModel, ActivityVisualTypesEnum, isMessageActivity} from '../../../models/api/activityModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {
  ApiActivitiesDataGroup,
  ApiActivityDataGroup,
  DataGroupModel,
} from '../../../models/api/dataGroupModel';
import {MessageVisualStatusesEnum} from '../../../models/api/messageModel';
import {fetchApiAsync} from '../../../services/http/http';
import {AppState} from '../../appState';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getHasCommentTombstone} from '../../featureFlags/featureFlagsSelectors';
import {getCompany} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getComments} from '../dataSelectors';

/*
 * Action types.
 */

export enum ActivitiesActionsEnum {
  ACTIVITY_DELETED = '@@data/activities/ACTIVITY_DELETED',
}

/*
 * Types.
 */

interface ActivityDeletedPayload {
  activity: ActivityModel;
  deletedAt: number;
  hasCommentTombstone: boolean;
}

export interface ActivityDeletedAction extends ActionWithPayload<ActivityDeletedPayload> {
  type: ActivitiesActionsEnum.ACTIVITY_DELETED;
}

/*
 * Action creators.
 */

export function createActivityFromPushAsyncAction(dataGroup: DataGroupModel): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the activity that we need to process.
    const {activities} = dataGroup;
    const activity = activities && activities[0];
    if (!activity) {
      return;
    }

    // For activities that aren't message or comment, we dispatch now.
    const isCommentActivity = activity.visualType === ActivityVisualTypesEnum.COMMENT;
    if (!isCommentActivity && !isMessageActivity(activity)) {
      dispatch(updateDataAction(dataGroup));
      return;
    }

    // Otherwise, we may need to fetch the message.
    await dispatch(fetchActivityAsyncAction(activity.conversationId, activity.id));
  };
}

export function fetchActivityAsyncAction(
  conversationId: number,
  activityId: number,
): ThunkAsyncAction<DataGroupModel> {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const fetchDataGroup = await fetchApiAsync(
      ApiActivityDataGroup,
      `${company.url}/conversations/${conversationId}/timeline/${activityId}`,
    );

    dispatch(updateDataAction(fetchDataGroup));

    return fetchDataGroup;
  };
}

export function fetchPinnedActivitiesAsyncAction(
  conversation: ConversationModel,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  return async (dispatch) => {
    const fetchDataGroup = await fetchApiAsync(ApiActivitiesDataGroup, `${conversation.url}/pinned`, {
      cancelToken,
    });
    dispatch(updateDataAction(fetchDataGroup));
  };
}

export function isActivityLoaded(state: AppState, activity: ActivityModel) {
  switch (activity.visualType) {
    case ActivityVisualTypesEnum.MESSAGE: {
      const message = state.data.messages[activity.messageUid];
      return Boolean(message && message.visualStatus !== MessageVisualStatusesEnum.PARTIAL);
    }

    case ActivityVisualTypesEnum.COMMENT: {
      const comments = getComments(state);
      return Boolean(comments[activity.commentUid]);
    }
    default:
      return true;
  }
}

export function deleteActivity(activityId: number, deletedAt: number): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing activity.
    const state = getState();
    const dataState = state.data;
    const existingActivity = dataState.activities[activityId];
    const hasCommentTombstone = getHasCommentTombstone(state);

    // If none was found, nothing to do.
    if (!existingActivity) {
      return null;
    }

    // Dispatch the activity delete action.
    const deleteActivityAction: ActivityDeletedAction = {
      type: ActivitiesActionsEnum.ACTIVITY_DELETED,
      payload: {
        activity: existingActivity,
        deletedAt,
        hasCommentTombstone,
      },
    };
    return dispatch(deleteActivityAction);
  };
}

/*
 * Union type.
 */

export type ActivitiesAction = ActivityDeletedAction;
